/**
 * Global rain manager.
 * Canvas-based rendering; scene ranges and drop counts come from story config (effects.rain).
 */

import { gsap } from 'gsap'
import { areAnimationsEnabled } from '../../utils/animation-settings'
import { RainAudioManager, type RainSfxConfig } from '../audio/RainAudioManager'
import { isScrollSoundsSuppressed } from '../scenes/animations/scroll-sound'

export interface RainConfig {
  from: [string, string]
  to: [string, string]
  minDrops: number
  maxDrops: number
  dropAngle: number
}

let dropCos = Math.cos(Math.PI / 4)
let dropSin = Math.sin(Math.PI / 4)

function setDropAngle(degrees: number): void {
  const rad = (degrees * Math.PI) / 180
  dropCos = Math.cos(rad)
  dropSin = Math.sin(rad)
}

/** Config drop counts are defined for this reference width; scale down/up for other viewports. */
const REFERENCE_WIDTH_PX = 2500

function getDropCountScale(): number {
  if (typeof window === 'undefined') return 1
  return window.innerWidth / REFERENCE_WIDTH_PX
}

const DEFAULT_RAIN_CONFIG: RainConfig = {
  from: ['prsi-na-hlavy', 'prsi-bezime'],
  to: ['jindra-uvadi-hru', 'je-po-desti'],
  minDrops: 10,
  maxDrops: 2000,
  dropAngle: 45,
}

interface DropState {
  startTime: number
  duration: number
  baseLeft: number
  baseTop: number
  length: number
  thickness: number
}

interface GlobalRainManager {
  init: () => void
  destroy: () => void
}

let rainConfig: RainConfig = DEFAULT_RAIN_CONFIG
let rainAudio: RainAudioManager | null = null
let rainContainer: HTMLDivElement | null = null
let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let dropStates: DropState[] = []
let currentNumVisibleDrops = 0
let scrollHandler: (() => void) | null = null
let resizeObserver: ResizeObserver | null = null
let fromStartElement: HTMLElement | null = null
let rainOverlayApplied = false
let isActive = false
let rafId: number | null = null

function getMoveDistance(): number {
  return Math.max(window.innerWidth, window.innerHeight) * 1.5
}

/**
 * Spawn over the full extended area (above, right, and inside viewport) so every part
 * of the canvas gets drops, including the center.
 */
function spawnPosition(moveDistance: number): { left: number; top: number } {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const horizTravel = moveDistance * dropSin
  const leftMax = vw + Math.max(80, horizTravel * 0.45)
  const topMin = -moveDistance * 0.2
  const topMax = vh + moveDistance * 0.05
  return {
    left: Math.random() * leftMax,
    top: topMin + Math.random() * (topMax - topMin),
  }
}

function createRainContainer(): HTMLDivElement {
  const container = document.createElement('div')
  container.className = 'global-rain-container'
  container.style.cssText =
    'position:fixed;top:0;right:0;bottom:0;left:0;pointer-events:none;z-index:3000;overflow:hidden;opacity:0;visibility:hidden'
  document.body.appendChild(container)
  return container
}

function resizeCanvas(): void {
  if (!canvas || !rainContainer || !ctx) return
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const w = rainContainer.clientWidth
  const h = rainContainer.clientHeight
  if (w <= 0 || h <= 0) return
  if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
    canvas.width = w * dpr
    canvas.height = h * dpr
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(dpr, dpr)
  }
}

function createCanvasAndDrops(container: HTMLDivElement): void {
  dropStates = []
  const moveDistance = getMoveDistance()

  const c = document.createElement('canvas')
  c.className = 'global-rain-canvas'
  c.style.cssText = 'display:block;width:100%;height:100%'
  container.appendChild(c)
  canvas = c
  ctx = c.getContext('2d', { alpha: true })
  resizeCanvas()

  const maxDrops = Math.min(rainConfig.maxDrops, 10000)
  for (let i = 0; i < maxDrops; i++) {
    const length = 15 + Math.random() * 40
    const thickness = 1 + Math.random() * 2
    const duration = 0.3 + Math.random() * 0.7
    const pos = spawnPosition(moveDistance)
    dropStates.push({
      startTime: 0,
      duration,
      baseLeft: pos.left,
      baseTop: pos.top,
      length,
      thickness,
    })
  }
}

function tickRain(): void {
  rafId = requestAnimationFrame(tickRain)

  if (!isActive || !canvas || !ctx || !rainContainer || dropStates.length === 0) return

  const vw = rainContainer.clientWidth
  const vh = rainContainer.clientHeight
  if (vw <= 0 || vh <= 0) return

  const moveDistance = Math.max(vw, vh) * 1.5
  const now = performance.now()

  ctx.clearRect(0, 0, vw, vh)
  ctx.strokeStyle = 'rgba(180, 200, 255, 0.85)'
  ctx.lineCap = 'round'

  const n = Math.min(currentNumVisibleDrops, dropStates.length)
  for (let i = 0; i < n; i++) {
    const state = dropStates[i]
    let startTime = state.startTime

    if (startTime === 0) {
      startTime = now
      state.startTime = startTime
    }

    const elapsedSec = (now - startTime) / 1000
    const progress = elapsedSec / state.duration

    if (progress >= 1) {
      const pos = spawnPosition(moveDistance)
      state.baseLeft = pos.left
      state.baseTop = pos.top
      state.startTime = now
      continue
    }

    const x = state.baseLeft - progress * moveDistance * dropSin
    const y = state.baseTop + progress * moveDistance * dropCos
    const dx = -state.length * dropSin
    const dy = state.length * dropCos

    ctx.lineWidth = state.thickness
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + dx, y + dy)
    ctx.stroke()
  }
}

function updateRainIntensity(intensity: number): void {
  if (!rainContainer) return

  const isInRange = intensity >= 0
  const clampedIntensity = isInRange ? Math.max(0, Math.min(1, intensity)) : 0
  const { minDrops, maxDrops } = rainConfig
  const configDrops = isInRange ? Math.floor(minDrops + clampedIntensity * (Math.min(maxDrops, 10000) - minDrops)) : 0
  const scale = getDropCountScale()
  const numVisibleDrops = Math.min(dropStates.length, Math.max(0, Math.floor(configDrops * scale)))
  currentNumVisibleDrops = numVisibleDrops

  if (isInRange) {
    rainContainer.style.visibility = 'visible'
    gsap.killTweensOf(rainContainer, 'opacity')
    gsap.to(rainContainer, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  } else {
    gsap.to(rainContainer, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.out',
      onComplete: () => {
        if (rainContainer && !isInRange) {
          rainContainer.style.visibility = 'hidden'
        }
      },
    })
  }
}

function findSceneElement(sceneId: string): HTMLElement | null {
  return document.querySelector(`.scene-wrapper[data-scene="${sceneId}"]`) as HTMLElement | null
}

/** For sticky scenes, the wrapper's rect is fixed while stuck; use the scroll container's rect. */
function getScrollReferenceElement(sceneElement: HTMLElement): HTMLElement {
  let el: HTMLElement | null = sceneElement
  while (el?.parentElement) {
    const p = el.parentElement as HTMLElement
    if (
      p.classList.contains('sticky-layout-container') ||
      p.classList.contains('scroll-animated-sticky-container') ||
      p.classList.contains('rain-sticky-container')
    ) {
      return p
    }
    el = p
  }
  return sceneElement
}

/**
 * Intensity from story config:
 * - Rain starts when from[0]'s center enters viewport center (0→1 ramp to from[1] center at viewport center).
 *   For sticky containers, uses the .scene-wrapper center once sticky.
 * - Rain starts decreasing (1→0) when to[0]'s center reaches viewport center.
 * - Rain stops when to[1]'s top reaches the top of the viewport.
 */
function calculateRainIntensity(): number | null {
  const [fromStartId, fromMaxId] = rainConfig.from
  const [toFadeStartId, toEndId] = rainConfig.to
  const vh = window.innerHeight
  const viewportCenter = vh / 2

  const fromStart = findSceneElement(fromStartId)
  const fromMax = findSceneElement(fromMaxId)
  const toEnd = findSceneElement(toEndId)

  if (!fromStart) return null

  const fromStartRect = fromStart.getBoundingClientRect()
  const fromStartCenterY = fromStartRect.top + fromStartRect.height / 2
  const fromRefEl = getScrollReferenceElement(fromStart)
  const inStickyContainer = fromRefEl !== fromStart

  let rainStarted = fromStartCenterY <= viewportCenter
  if (!rainStarted && inStickyContainer) {
    // For sticky containers: the wrapper's center may not be at viewport center
    // if the image hasn't loaded yet (incorrect sticky top on initial render).
    // Fall back to checking whether the container has scrolled into the sticky zone.
    const fromRefRect = fromRefEl.getBoundingClientRect()
    const stickyOffset = fromStartRect.height > 0
      ? viewportCenter - fromStartRect.height / 2
      : 0
    rainStarted = fromRefRect.top <= stickyOffset
  }

  if (!rainStarted) return null

  // to[1] fully off once its top reaches viewport center
  if (toEnd) {
    const toEndRect = toEnd.getBoundingClientRect()
    if (toEndRect.top <= viewportCenter) return null
  }

  if (!fromMax) return 0

  const fromMaxCenterY = fromMax.getBoundingClientRect().top + fromMax.getBoundingClientRect().height / 2

  if (fromMaxCenterY > viewportCenter) {
    const scrollY = window.scrollY
    const fromRefRect = fromRefEl.getBoundingClientRect()

    const scrollY_start = scrollY + fromRefRect.top - viewportCenter + fromStartRect.height / 2
    const scrollY_max = scrollY + fromMaxCenterY - viewportCenter
    const span = scrollY_max - scrollY_start

    if (span <= 0) return 1
    return Math.max(0, Math.min(1, (scrollY - scrollY_start) / span))
  }

  // Past max — compute base intensity from to[0] fade, then apply to[1] fade-out on top.
  const REDUCED_INTENSITY = 0.4
  const toFadeStart = findSceneElement(toFadeStartId)

  let baseIntensity = 1

  if (toFadeStart) {
    const toFadeStartTop = toFadeStart.getBoundingClientRect().top

    if (toFadeStartTop <= viewportCenter) {
      baseIntensity = REDUCED_INTENSITY
    } else if (toFadeStartTop < vh) {
      const progress = Math.max(0, Math.min(1, (vh - toFadeStartTop) / viewportCenter))
      baseIntensity = 1 - progress * (1 - REDUCED_INTENSITY)
    }
  }

  // Smooth fade-out: to[1] top from vh → viewportCenter maps intensity from baseIntensity → 0
  if (toEnd) {
    const toEndTop = toEnd.getBoundingClientRect().top
    if (toEndTop < vh) {
      const fadeProgress = Math.max(0, Math.min(1, (vh - toEndTop) / viewportCenter))
      baseIntensity *= (1 - fadeProgress)
    }
  }

  return baseIntensity
}

function applyRainOverlayZIndex(): void {
  if (rainOverlayApplied) return
  const el = findSceneElement(rainConfig.to[0])
  if (!el) return
  el.style.position = 'relative'
  el.style.zIndex = '3001'
  rainOverlayApplied = true
}

function updateRain(): void {
  if (isScrollSoundsSuppressed()) {
    rainAudio?.updateIntensity(null)
    if (isActive) {
      isActive = false
      updateRainIntensity(-1)
    }
    return
  }
  applyRainOverlayZIndex()
  const intensity = calculateRainIntensity()

  rainAudio?.updateIntensity(intensity)

  if (intensity === null) {
    if (isActive) {
      isActive = false
      updateRainIntensity(-1)
    }
    return
  }

  if (!isActive) isActive = true
  updateRainIntensity(intensity)
}

export function setRainAudioVolume(v: number): void {
  rainAudio?.setVolume(v)
}

export function createGlobalRainManager(config?: Partial<RainConfig> | null, rainSfxConfig?: RainSfxConfig): GlobalRainManager {
  if (config && config.from?.length === 2 && config.to?.length === 2) {
    rainConfig = {
      from: config.from,
      to: config.to,
      minDrops: config.minDrops ?? DEFAULT_RAIN_CONFIG.minDrops,
      maxDrops: config.maxDrops ?? DEFAULT_RAIN_CONFIG.maxDrops,
      dropAngle: config.dropAngle ?? DEFAULT_RAIN_CONFIG.dropAngle,
    }
  } else {
    rainConfig = { ...DEFAULT_RAIN_CONFIG }
  }
  setDropAngle(rainConfig.dropAngle)

  return {
    init() {
      rainAudio = new RainAudioManager(rainSfxConfig ?? { soft: 'rain-soft.mp3', strong: 'rain-strong.mp3' })

      if (!areAnimationsEnabled()) return

      rainContainer = createRainContainer()
      createCanvasAndDrops(rainContainer)

      let retryCount = 0
      const MAX_RETRIES = 100
      const findScenes = () => {
        retryCount++
        fromStartElement = findSceneElement(rainConfig.from[0])

        if (fromStartElement) {
          updateRain()
        } else if (retryCount < MAX_RETRIES) {
          requestAnimationFrame(findScenes)
        }
      }

      requestAnimationFrame(() => requestAnimationFrame(findScenes))

      if (rainContainer) gsap.set(rainContainer, { opacity: 0 })

      scrollHandler = () => {
        if (!fromStartElement) {
          fromStartElement = findSceneElement(rainConfig.from[0])
        }
        const inOrNearRainZone = fromStartElement && fromStartElement.getBoundingClientRect().top < window.innerHeight
        if (isActive || inOrNearRainZone) {
          updateRain()
        }
      }
      window.addEventListener('scroll', scrollHandler, { passive: true })

      resizeObserver = new ResizeObserver(() => {
        resizeCanvas()
        fromStartElement = findSceneElement(rainConfig.from[0])
        if (isActive || fromStartElement) updateRain()
      })
      if (rainContainer) resizeObserver.observe(rainContainer)
      if (document.body) resizeObserver.observe(document.body)

      rafId = requestAnimationFrame(tickRain)
    },

    destroy() {
      if (rainAudio) {
        rainAudio.destroy()
        rainAudio = null
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler)
        scrollHandler = null
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (rainContainer) {
        gsap.to(rainContainer, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => {
            if (rainContainer?.parentNode) rainContainer.parentNode.removeChild(rainContainer)
            rainContainer = null
            canvas = null
            ctx = null
          },
        })
      } else {
        canvas = null
        ctx = null
      }
      dropStates = []
      fromStartElement = null
      rainOverlayApplied = false
      currentNumVisibleDrops = 0
      isActive = false
    },
  }
}
